import { Navigate, useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { getDevSession } from '@/lib/dev-login'

/** Checks if current user has an admin role for the current tenant */
export function RequireTenantAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { slug } = useParams<{ slug: string }>()
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user || !slug) {
      setLoading(false)
      setIsAdmin(false)
      return
    }

    const devSession = getDevSession()
    if (devSession) {
      setIsAdmin(true)
      setLoading(false)
      return
    }

    const check = async () => {
      try {
        // Single query: resolve tenant by slug and check admin role in one round trip
        const { data: role } = await supabase
          .from('user_roles')
          .select('id, tenants!inner(slug)')
          .eq('tenants.slug', slug)
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle()

        setIsAdmin(!!role)
      } catch {
        setIsAdmin(false)
      } finally {
        setLoading(false)
      }
    }

    check()
  }, [user, slug])

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-white/30" />
      </div>
    )
  }

  if (!isAdmin) {
    return <Navigate to="/origin/login" replace />
  }

  return <>{children}</>
}
